// Salmon of Knowledge pure helper. No React, no Three.js.
//
// ── Codex (Salmon of Knowledge, Minion) ──
//   "Submerge, Waterbound. Other units at this site have 'Tap → Roll a
//   d20. On a 14+, banish Salmon of Knowledge and draw three spells.'"
//
// The "other units at this site have [activated]" clause is installed
// via grant_trigger_to_scope (outer scope `here`, exclude_self). Every
// unit sharing Salmon's cell gets the granted activated entry, stamped
// with `_sourceCardId` pointing back at Salmon. When a granted unit
// fires the ability, the activating unit's controller rolls a d20: on a
// roll >= threshold the granting Salmon is banished and the controller
// draws `draw` spells.
//
// Dice sync follows the engine's established random pattern (Sparkmage,
// Thunderstorm): the roll resolves locally on the acting client and the
// OUTCOME. The banish (a synced card move) and the draw (a synced
// pile/hand mutation). Propagates over the wire. The acting client
// also broadcasts the rolled number for a shared on-screen log.
//
// Host adapter contract:
//   rollDie(sides): number. Uniform integer in [1, sides]
//   findCardById(id): card | null. Locate the granting Salmon
//   banishCard(card): void
//   drawSpellsFor(controller, count): number
//   logAction(key, values?): void

import { msg } from '../../../../logMessage';

/**
 * Resolve a granted Salmon roll-ability activation.
 *
 * @param {object} unit         the unit that activated the ability
 * @param {object} grantedEntry the granted automation entry (carries
 *                              `_sourceCardId` = Salmon's instance id)
 * @param {object} args         { threshold: 14, draw: 3, die: 20 }
 * @param {string} controller   'p1' | 'p2'. The activating unit's controller
 * @param {object} host
 * @returns {{ roll: number, hit: boolean }}
 */
export function applyRollBanishSourceDraw(unit, grantedEntry, args, controller, host) {
  const die = args?.die || 20;
  const threshold = args?.threshold ?? 14;
  const drawCount = args?.draw ?? 3;
  const roll = host.rollDie(die);
  const hit = roll >= threshold;
  const label = unit?.name || msg('A unit');
  const rolled = { name: label, die, roll, threshold };
  if (hit) host.logAction('{name} rolls a d{die}: {roll} (>= {threshold})', rolled);
  else host.logAction('{name} rolls a d{die}: {roll} (< {threshold})', rolled);
  if (!hit) return { roll, hit };

  const salmon = grantedEntry?._sourceCardId ? host.findCardById(grantedEntry._sourceCardId) : null;
  if (salmon) {
    host.logAction('{card} is banished', { card: salmon.name || msg('Salmon of Knowledge') });
    host.banishCard(salmon);
  }
  const drawn = host.drawSpellsFor(controller, drawCount);
  if (drawn > 0) {
    host.logAction('{name}: draws {n} spells', { name: label, n: drawn });
  }
  return { roll, hit };
}
